import {
	createSlice,
	createEntityAdapter,
	createAsyncThunk,
	createSelector,
} from "@reduxjs/toolkit";
import {
	getLeaveLinesByFormId,
	postLeaveLines as post,
	deleteLeaveLineByFormId,
} from "api";
import dayjs from "dayjs";

export const validateAllLeaveLines = createAsyncThunk(
	"leaveLines/validateAllLeaveLines",
	async (_, { dispatch, getState }) => {
		const state = getState();
		const leaveLines = selectAllLeaveLines(state);
		const errors = validateLeaveLines(leaveLines);
		dispatch(leaveErrorEdited(errors));
		dispatch(validationErrorsEdited(errors.length));
		return errors.length;
	}
);

const getMaxLeaveUnitsByType = (type) => {
	const ucaseType = type.toUpperCase();
	switch (ucaseType) {
		case "DAYS":
			return 1;
		case "HOURS":
			return 8;
		default:
			return 0;
	}
};

const validateLeaveLines = (data) => {
	let errors = [];
	if (!data || data.length === 0) {
		errors.push(
			"Please fill out at least one day of leave details to continue."
		);
	} else {
		data.forEach((l) => {
			const maxUnit = getMaxLeaveUnitsByType(l.unitType);
			if (l.numberOfUnits > maxUnit)
				errors.push(
					`You cannot have more than ${maxUnit} unit(s) in a single day.`
				);
		});

		const total = data.reduce((acc, l) => acc + l.numberOfUnits, 0);
		if (total === 0)
			errors.push(
				"Please fill out at least a partial day of leave to continue."
			);
	}

	return errors;
};

export const fetchLeaveLinesByFormId = createAsyncThunk(
	"leaveLines/fetchLeaveLinesByFormId",
	//need the thunkApi's getState to retrieve auth token
	async (formId, { rejectWithValue, dispatch }) => {
		try {
			const data = await getLeaveLinesByFormId({ formId });
			dispatch(leaveLinesReceived(data));
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postLeaveLines = createAsyncThunk(
	"leaveLines/postLeaveLines",
	async (data, { dispatch, rejectWithValue }) => {
		try {
			const response = await post(data);
			dispatch(leaveLinesReceived(response));
			return response;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const deleteLeaveLines = createAsyncThunk(
	"leaveLines/deleteLeaveLines",
	async (data, { getState, dispatch, rejectWithValue }) => {
		try {
			await deleteLeaveLineByFormId(data);
			const state = getState();
			dispatch(leaveLinesRemoved(state));
			return true;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const getNewLeaveLines = ({ formId, from, to, type }) => {
	const a = dayjs(from);
	const b = dayjs(to);
	const dateDiff = b.diff(a, "days");

	let dates = [];

	for (let counter = 0; counter <= dateDiff; counter++) {
		dates.push({
			...LEAVE_LINE_TEMPLATE,
			leaveDate: a.add(counter, "days").format("YYYY-MM-DD"),
			formId: formId,
			unitType: type === "smoLeaveRequest" ? "Hours" : "Days",
		});
	}

	return dates;
};

const leaveLinesAdapter = createEntityAdapter({
	selectId: (leaveLine) => leaveLine.leaveId,
});

export const LEAVE_LINE_TEMPLATE = {
	leaveId: 0,
	leaveDate: null,
	parentId: 0,
	formId: 0,
	statusId: 0,
	categoryLookupId: null,
	shiftLookupId: 0,
	description: null,
	comments: null,
	numberOfUnits: null,
	unitType: "", //populated elsewhere
	isDeleted: false,
	errors: [],
};

const initialState = leaveLinesAdapter.getInitialState({
	status: "starting",
	error: null,
	validationErrors: 0,
});

const leaveLineSlice = createSlice({
	name: "leaveLines",
	initialState,
	reducers: {
		leaveLinesRemoved(state, action) {
			leaveLinesAdapter.removeAll(state);
		},
		leaveLinesReceived(state, action) {
			leaveLinesAdapter.addMany(state, action.payload);
		},
		leaveLineEdited(state, action) {
			const { questionId, id, value } = action.payload;
			const req = { leaveId: id, [questionId]: value };
			leaveLinesAdapter.upsertOne(state, req);
		},
		leaveLineAdded(state, action) {
			const { questionId, value } = action.payload;
			const req = { leaveId: 0, [questionId]: value }; //zero signifies new record
			leaveLinesAdapter.addOne(state, { ...req, ...LEAVE_LINE_TEMPLATE });
		},
		leaveLineDeleted(state, action) {
			leaveLinesAdapter.removeOne(state, action.payload);
		},
		validationErrorsEdited(state, action) {
			state.validationErrors = action.payload;
		},
		leaveErrorEdited(state, action) {
			state.error = action.payload;
		},
	},
	extraReducers: {
		RESET_CME: (state, action) => {
			return initialState;
		},
		[postLeaveLines.pending]: (state, action) => {
			state.status = "loading";
			state.error = null;
		},
		[postLeaveLines.fulfilled]: (state, action) => {
			state.status = "succeeded";
			state.error = null;
		},
		[postLeaveLines.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[fetchLeaveLinesByFormId.pending]: (state, action) => {
			state.status = "loading";
			state.error = null;
		},
		[fetchLeaveLinesByFormId.fulfilled]: (state, action) => {
			state.status = "succeeded";
			state.error = null;
		},
		[fetchLeaveLinesByFormId.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[deleteLeaveLines.pending]: (state, action) => {
			state.status = "loading";
			state.error = null;
		},
		[deleteLeaveLines.fulfilled]: (state, action) => {
			state.status = "succeeded";
			state.error = null;
		},
		[deleteLeaveLines.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
	},
});

export const {
	leaveLineEdited,
	leaveLinesReceived,
	leaveLineAdded,
	leaveLinesRemoved,
	validationErrorsEdited,
	leaveLineDeleted,
	leaveErrorEdited,
} = leaveLineSlice.actions;

export const {
	selectAll: selectAllLeaveLines,
	selectIds: selectLeaveLineIds,
	selectById: selectLeaveLineById,
} = leaveLinesAdapter.getSelectors((state) => state?.cme?.leaveLines);

export const selectLeaveLinesStatus = (state) => state.cme.leaveLines.status;

export const selectLeaveError = (state) => state.cme.leaveLines.error;

export const selectLeaveAmount = createSelector([selectAllLeaveLines], (l) => {
	return l.reduce((acc, i) => {
		return i.categoryLookupId === CME ? acc + i.numberOfUnits : acc;
	}, 0);
});

export const selectLeaveUnitType = createSelector(
	[selectAllLeaveLines],
	(l) => {
		if (l.length > 0) {
			return l[0].unitType;
		} else return "Days";
	}
);

export const CME = 246;
export const CME_TRAVEL = 247;

export default leaveLineSlice.reducer;
